import { useQuery } from '@tanstack/react-query';
import { getResults } from '@/api/resultsApi';
import type { ResultsResponse } from '@/api/resultsApi';
import type { AxiosError } from 'axios';

interface UseResultsPollingReturn {
  /** Risultati completi, null se non ancora pronti */
  data: ResultsResponse | null;
  /** True se in attesa dei risultati */
  isLoading: boolean;
  /** Messaggio di errore, null se ok */
  error: string | null;
  /** Forza un refresh immediato */
  refetch: () => void;
}

/**
 * Hook per effettuare il polling dei risultati finché non sono disponibili.
 * Interroga l'endpoint /api/results/{videoId} ogni 5 secondi.
 * Gestisce il 404 come "non ancora pronto" senza considerarlo errore.
 */
export function useResultsPolling(
  videoId: string | null
): UseResultsPollingReturn {
  const query = useQuery<ResultsResponse | null, AxiosError>({
    queryKey: ['results', videoId],
    queryFn: async () => {
      try {
        return await getResults(videoId!);
      } catch (err: any) {
        // 404 = risultati non ancora disponibili → non è un errore, ma "ancora in elaborazione"
        if (err?.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    enabled: !!videoId,
    staleTime: 0,            // considera sempre i dati come obsoleti per forzare il refetch
    refetchInterval: 5000,   // polling ogni 5 secondi
    retry: false,            // non ritentare automaticamente gli errori reali
  });

  return {
    data: query.data ?? null,
    isLoading: query.isLoading || (query.data === null && !query.error),
    error: query.error ? (query.error.message ?? 'Errore sconosciuto') : null,
    refetch: query.refetch,
  };
}